import { z } from "zod";

/**
 * Schema for the runtime configuration contract published at /config.js.
 *
 * This schema is evaluated at app boot; its failure modes are user-visible
 * errors rather than silently-degraded UI. A misconfigured ConfigMap stops
 * the SPA before it can authenticate against an unintended tenant.
 */
const AppConfigSchema = z.object({
    msal: z.object({
        clientId: z.string().min(1, "msal.clientId must be a non-empty string"),
        authority: z.string().url("msal.authority must be an absolute URL"),
        apiScope: z.string().min(1, "msal.apiScope must be a non-empty string"),
    }),
});

export type AppConfig = z.infer<typeof AppConfigSchema>;

/**
 * Loads and validates the runtime configuration from `window.__APP_CONFIG__`.
 *
 * Throws a descriptive `Error` when the contract is not satisfied. The
 * message lists every invalid field so the operator can fix the ConfigMap
 * (or local placeholder) without guessing.
 */
export function loadAppConfig(): AppConfig {
    if (typeof window === "undefined") {
        throw new Error(
            "Runtime configuration cannot be loaded: `window` is undefined. " +
                "authConfig.loadAppConfig() must be called in a browser context.",
        );
    }

    const raw = window.__APP_CONFIG__;
    if (raw === undefined || raw === null) {
        throw new Error(
            "Runtime configuration missing at /config.js: window.__APP_CONFIG__ is undefined. " +
                "Verify that /config.js is served before the app bundle and sets window.__APP_CONFIG__.",
        );
    }

    const result = AppConfigSchema.safeParse(raw);
    if (!result.success) {
        const details = result.error.issues
            .map((issue) => {
                const path = issue.path.length > 0 ? issue.path.join(".") : "(root)";
                return `  - ${path}: ${issue.message}`;
            })
            .join("\n");
        throw new Error(
            "Runtime configuration at /config.js is invalid. " +
                "The following fields failed validation:\n" +
                details,
        );
    }

    return result.data;
}

// Exported for tests that want to validate arbitrary objects without the
// module-level side-effect of reading `window.__APP_CONFIG__`.
export { AppConfigSchema };
